import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Plus, Server } from "lucide-react";
import { ApiError, type WispRadiusServerDto } from "@finora/api-client";
import { Button, EmptyState, Skeleton } from "@finora/ui";
import { api } from "../../lib/api";

const inputCls = "h-10 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none";
const labelCls = "mb-1 block text-xs font-medium text-slate-600";

function errMsg(err: unknown, fallback: string) {
  if (err instanceof ApiError) {
    const m = err.response.message;
    return Array.isArray(m) ? m.join(" ") : m;
  }
  return fallback;
}

function fmtDateTime(v: string | null) {
  if (!v) return "nikad";
  return new Date(v).toLocaleString("sr-Latn-RS", { dateStyle: "short", timeStyle: "medium" });
}

function stateOf(s: WispRadiusServerDto) {
  const a = s.agent;
  if (!a.tokenSet) return { cls: "bg-slate-100 text-slate-600", text: "Nije instaliran" };
  if (a.online && a.radiusActive) return { cls: "bg-emerald-50 text-emerald-800", text: "Radi" };
  if (a.online) return { cls: "bg-amber-50 text-amber-800", text: "Agent radi, FreeRADIUS ne" };
  if (!a.lastSeenAt) return { cls: "bg-amber-50 text-amber-800", text: "Čeka instalaciju" };
  return { cls: "bg-red-50 text-red-700", text: `Ne javlja se od ${fmtDateTime(a.lastSeenAt)}` };
}

/** RADIUS serveri firme: firma sama dodaje svoje servere i instalira ih jednom komandom. */
export function ServersTab() {
  const qc = useQueryClient();
  const list = useQuery({ queryKey: ["wisp-servers"], queryFn: () => api.listWispServers(), refetchInterval: 10_000 });
  const [f, setF] = useState({ name: "", host: "" });
  const [err, setErr] = useState<string | null>(null);
  const done = () => {
    setErr(null);
    qc.invalidateQueries({ queryKey: ["wisp-servers"] });
    qc.invalidateQueries({ queryKey: ["wisp-overview"] });
  };
  const create = useMutation({
    mutationFn: () => api.createWispServer({ name: f.name, host: f.host || null }),
    onSuccess: () => {
      setF({ name: "", host: "" });
      done();
    },
    onError: (e) => setErr(errMsg(e, "Dodavanje nije uspelo.")),
  });

  return (
    <div className="space-y-3">
      <div className="grid gap-3 rounded-xl border border-border bg-surface p-4 shadow-subtle md:grid-cols-3">
        <div>
          <label className={labelCls}>Naziv servera</label>
          <input className={inputCls} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="npr. Glavni RADIUS" />
        </div>
        <div>
          <label className={labelCls}>Javna adresa (domen ili IP)</label>
          <input className={inputCls} value={f.host} onChange={(e) => setF({ ...f, host: e.target.value })} placeholder="radius.mojafirma.rs" />
        </div>
        <div className="flex items-end">
          <Button onClick={() => create.mutate()} disabled={create.isPending || !f.name.trim()}>
            <Plus className="mr-1 h-4 w-4" /> Dodaj server
          </Button>
        </div>
      </div>
      <p className="text-xs text-slate-500">
        RADIUS server je tvoja Debian ili Ubuntu mašina. Posle dodavanja klikni „Ključ i komanda za instalaciju" i pokreni dobijenu komandu na
        serveru kao root - sve ostalo se podesi samo. Ruteri se na server povezuju bezbednim tunelom preko UDP porta tunela (podrazumevano 51820).
      </p>
      {err && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</p>}

      {list.isLoading ? (
        <Skeleton className="h-32 w-full" />
      ) : !list.data?.length ? (
        <EmptyState icon={Server} title="Nema RADIUS servera" description="Dodaj server na koji će se povezivati tvoji MikroTik ruteri." />
      ) : (
        list.data.map((s) => <ServerCard key={s.id} s={s} onChanged={done} />)
      )}
    </div>
  );
}

function ServerCard({ s, onChanged }: { s: WispRadiusServerDto; onChanged: () => void }) {
  const [f, setF] = useState({
    name: s.name,
    host: s.host ?? "",
    wgPort: String(s.wgPort),
    tunnelNetwork: s.tunnelNetwork,
    authPort: String(s.authPort),
    acctPort: String(s.acctPort),
    coaPort: String(s.coaPort),
  });
  const [open, setOpen] = useState(!s.agent.tokenSet);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [install, setInstall] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const st = stateOf(s);

  const save = useMutation({
    mutationFn: () =>
      api.updateWispServer(s.id, {
        name: f.name,
        host: f.host || null,
        wgPort: Number(f.wgPort),
        tunnelNetwork: f.tunnelNetwork,
        authPort: Number(f.authPort),
        acctPort: Number(f.acctPort),
        coaPort: Number(f.coaPort),
      }),
    onSuccess: () => {
      setMsg({ ok: true, text: "Sačuvano." });
      onChanged();
    },
    onError: (e) => setMsg({ ok: false, text: errMsg(e, "Čuvanje nije uspelo.") }),
  });
  const token = useMutation({
    mutationFn: () => api.rotateWispServerToken(s.id),
    onSuccess: (r) => {
      setCopied(false);
      setInstall(r.installCommand);
      onChanged();
    },
    onError: (e) => setMsg({ ok: false, text: errMsg(e, "Nije uspelo.") }),
  });
  const remove = useMutation({
    mutationFn: () => api.deleteWispServer(s.id),
    onSuccess: onChanged,
    onError: (e) => setMsg({ ok: false, text: errMsg(e, "Brisanje nije uspelo.") }),
  });
  const set = (k: keyof typeof f, v: string) => setF({ ...f, [k]: v });

  return (
    <div className="space-y-3 rounded-xl border border-border bg-surface p-4 shadow-subtle">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="font-semibold text-slate-900">{s.name}</div>
          <div className="text-xs text-slate-500">
            {s.host ?? "adresa nije upisana"} · ruteri {s.nasCount}
            {s.agent.hostname ? ` · ${s.agent.hostname}` : ""}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className={`rounded-full px-3 py-1 text-xs font-medium ${st.cls}`}>{st.text}</span>
          <button type="button" className="text-xs text-brand underline" onClick={() => setOpen(!open)}>
            {open ? "zatvori" : "podešavanja"}
          </button>
        </div>
      </div>
      {s.agent.lastError && <p className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">{s.agent.lastError}</p>}

      {open && (
        <>
          <div className="grid gap-3 md:grid-cols-3">
            <div>
              <label className={labelCls}>Naziv</label>
              <input className={inputCls} value={f.name} onChange={(e) => set("name", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Javna adresa (domen ili IP)</label>
              <input className={inputCls} value={f.host} onChange={(e) => set("host", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Port tunela (UDP)</label>
              <input className={inputCls} value={f.wgPort} onChange={(e) => set("wgPort", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Mreža tunela</label>
              <input className={inputCls} value={f.tunnelNetwork} onChange={(e) => set("tunnelNetwork", e.target.value)} disabled={s.nasCount > 0} />
              <p className="mt-1 text-xs text-slate-400">
                {s.nasCount > 0 ? "Ne menja se dok na serveru ima rutera." : `Server dobija ${s.serverTunnelIp ?? "prvu adresu"}.`}
              </p>
            </div>
            <div>
              <label className={labelCls}>Port za prijavu</label>
              <input className={inputCls} value={f.authPort} onChange={(e) => set("authPort", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Port za merenje saobraćaja</label>
              <input className={inputCls} value={f.acctPort} onChange={(e) => set("acctPort", e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>Port za prekid sesije (na ruteru)</label>
              <input className={inputCls} value={f.coaPort} onChange={(e) => set("coaPort", e.target.value)} />
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => save.mutate()} disabled={save.isPending}>
              Sačuvaj
            </Button>
            <Button
              variant="secondary"
              onClick={() =>
                (!s.agent.tokenSet ||
                  window.confirm("Napraviti novi ključ? Server sa starim ključem prestaje da radi dok se nova komanda ne pokrene na njemu.")) &&
                token.mutate()
              }
            >
              Ključ i komanda za instalaciju
            </Button>
            {s.nasCount === 0 && (
              <Button variant="secondary" onClick={() => window.confirm(`Obrisati server "${s.name}"?`) && remove.mutate()}>
                Obriši
              </Button>
            )}
          </div>
        </>
      )}

      {install && (
        <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900">
          <p className="mb-1 font-medium">Pokreni na serveru (Debian/Ubuntu) kao root. Ključ se prikazuje samo sada.</p>
          <div className="flex items-start gap-2">
            <code className="flex-1 select-all break-all rounded bg-white px-2 py-1 text-xs">{install}</code>
            <button
              type="button"
              className="flex items-center gap-1 text-xs underline"
              onClick={() => void navigator.clipboard.writeText(install).then(() => setCopied(true))}
            >
              <Copy className="h-3 w-3" /> {copied ? "Kopirano" : "Kopiraj"}
            </button>
          </div>
        </div>
      )}
      {msg && <p className={`rounded-lg px-3 py-2 text-sm ${msg.ok ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-700"}`}>{msg.text}</p>}
    </div>
  );
}
